import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useResponseNotification from 'hooks/useResponseNotification';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from '../store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from '../store/declarations/enums/common/declaration-types';
import { getProductTemplateClient } from '../store/products-templates/getProductTemplateClient';
import { ProductTemplate } from '../store/products-templates/products';
import { createProductTemplatesNotifs, editProductTemplatesNotifs } from '../utils/notificationMessages';
import useFormUtils from './useFormUtils';

interface UseProductsTemplatesProps {
    country?: DeclarationCountry;
    internalType?: DeclarationInternalType;
    messageType?: MessageTypes;
}

const resolveToUndefinedOnError = async <T>(request: () => Promise<T>) => {
    try {
        return await request();
    } catch {
        return undefined;
    }
};

const useProductsTemplates = ({
    country: countryOverride,
    internalType: internalTypeOverride,
    messageType: messageTypeOverride,
}: UseProductsTemplatesProps = {}) => {
    const { withResponseNotifications } = useResponseNotification();
    const { t } = useTranslation('common');
    const { countryLowerCase, internalType: urlInternalType, formType: urlMessageType } = useFormUtils();
    const country = countryOverride ?? (countryLowerCase as DeclarationCountry);
    const internalType = internalTypeOverride ?? (urlInternalType as DeclarationInternalType);
    const messageType = messageTypeOverride ?? (urlMessageType as MessageTypes);

    const client = useMemo(
        () =>
            country && internalType && messageType
                ? getProductTemplateClient(country, internalType, messageType)
                : undefined,
        [country, internalType, messageType]
    );

    const saveProductTemplate = useCallback(
        async (product: ProductTemplate) => {
            if (!client) throw new Error('No data provided for save product template function.');

            const saved = await resolveToUndefinedOnError(() => client.save(product));
            return withResponseNotifications(saved, createProductTemplatesNotifs(t));
        },
        [client, withResponseNotifications, t]
    );

    const editProductTemplate = useCallback(
        async (product: ProductTemplate) => {
            if (!client) throw new Error('No data provided for edit product template function.');

            const updated = await resolveToUndefinedOnError(() => client.update(product));
            return withResponseNotifications(updated, editProductTemplatesNotifs(t));
        },
        [client, withResponseNotifications, t]
    );

    const deleteProductTemplate = useCallback(
        (productId: string) => {
            if (!(productId && client)) throw new Error('No data provided for delete product template function.');

            return client.remove(productId);
        },
        [client]
    );

    return { saveProductTemplate, editProductTemplate, deleteProductTemplate };
};

export default useProductsTemplates;
